const { getElement } = window.BAO.Utils
class LazyVideo extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: contents;

        opacity: 0;
      
        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.video = getElement('video', { context: this })

    this.hasNativeSupport = this.video.canPlayType(
      'application/vnd.apple.mpegurl',
    )

    // Always create and observe the video for pause/resume
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'DOMContentLoaded', this.onLoad.bind(this))
    this.listeners.add(window, 'resize', this.onResize.bind(this))
  }

  async onLoad () {
    // Always observe the video for pause/resume
    this.observer.observe(this.video)

    if (!this.deferVideo) {
      const hlsSource = this.hlsSource
      if (!hlsSource || (this.hasNativeSupport && hlsSource.src)) return
      this.handleHlsVideo(this.hlsSource)
    }
  }

  onEntry ([entry], observer) {
    if (entry.isIntersecting) {
      // If deferred, load sources when in view
      if (this.deferVideo && this.video.readyState === 0) {
        for (const source in this.video.children) {
          const videoSource = this.video.children[source]
          if (
            typeof videoSource.tagName === 'string' &&
            videoSource.tagName === 'SOURCE'
          ) {
            videoSource.src = videoSource.dataset.src
          }
          if (videoSource.type === 'application/x-mpegURL') {
            this.handleHlsVideo(videoSource)
          }
        }
        this.video.load()
        this.setAttribute('loaded', '')
      }
      // Resume video if paused and autoplay is not disabled
      if (this.video.paused && !this.dataset.disableAutoplay) {
        this.video.play().catch(() => {
          // If play is blocked (e.g. mobile), allow user interaction to trigger
          document.addEventListener(
            'touchstart',
            () => {
              if (this.video.paused) {
                this.video.play()
              }
            },
            { once: true },
          )
        })
      }
    } else {
      // Pause video when out of view
      if (!this.video.paused) {
        this.video.pause()
      }
    }
  }

  onResize () {
    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (videoSource.type === 'application/x-mpegURL') {
        this.handleHlsVideo(videoSource)
      }
    }
  }

  async handleHlsVideo (videoSource) {
    if (!videoSource) return

    if (!this.video.checkVisibility()) return

    if (this.hls) return

    /* global Hls */
    // eslint-disable-next-line import/no-unresolved
    await import('https://cdn.jsdelivr.net/npm/hls.js@latest')

    this.hls = new Hls({
      enableWorker: true,
      capLevelToPlayerSize: true,
      testBandwidth: false,
      startLevel: 1,
      autoStartLoad: true,
      maxBufferLength: 2,
      maxMaxBufferLength: 2,
    })

    const src = videoSource.src || videoSource.dataset.src
    if (!src) return

    this.hls.loadSource(src)
    this.hls.attachMedia(this.video)
    this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.video.play()
    })
  }

  get deferVideo () {
    return this.dataset.defer === 'true'
  }

  get hlsSource () {
    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (videoSource.type === 'application/x-mpegURL') {
        return videoSource
      }
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}
